import React, { useEffect } from 'react';
import { pinnedApps } from '../data/apps';
import { useAuth } from '../contexts/AuthContext';

interface RecentApp {
  id: string;
  name: string;
  icon: string;
  isCustomIcon?: boolean;
  lastUsed: Date;
}

interface StartMenuProps {
  onClose: () => void;
  onAppClick: (appId: string) => void;
  recentApps: RecentApp[];
}

/**
 * Windows-style start menu overlay with pinned programs, recently used apps, and sign-in/sign-out controls.
 * Closes automatically when clicking outside.
 * @param {StartMenuProps} props
 * @param {function} props.onClose - Callback to close the menu.
 * @param {function} props.onAppClick - Callback invoked with an app ID when a program is clicked.
 * @param {RecentApp[]} props.recentApps - List of recently opened apps to display.
 */
const StartMenu: React.FC<StartMenuProps> = ({ onClose, onAppClick, recentApps }) => {
  const { user, signIn, signOut } = useAuth();

  const formatLastUsed = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const startMenu = document.querySelector('.start-menu');
      const startButton = document.querySelector('.start-button');

      if (startMenu && startButton &&
          !startMenu.contains(event.target as Node) &&
          !startButton.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div className="start-menu">
      <div className="start-menu-header">
        <div className="user-profile">
          {user ? (
            <>
              {user.photoURL ? (
                <img className="user-avatar-img" src={user.photoURL} alt="" />
              ) : (
                <div className="user-avatar">
                  {user.displayName?.charAt(0) || '?'}
                </div>
              )}
              <span className="username">{user.displayName || user.email}</span>
            </>
          ) : (
            <>
              <div className="user-avatar">JZ</div>
              <span className="username">Jake Zhao</span>
            </>
          )}
        </div>
      </div>

      <div className="start-menu-content">
        <div className="programs-section">
          <h3>Programs</h3>
          <div className="programs-list">
            {pinnedApps.map((app) => (
              <div key={app.id} className="program-item" onClick={() => {
                onAppClick(app.id);
                onClose();
              }}>
                <div className="program-icon">
                  {app.isCustomIcon ? (
                    <img
                      src={app.icon}
                      alt={app.name}
                      width="24"
                      height="24"
                      style={{
                        display: 'block',
                        filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))'
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = '🔗';
                          parent.style.fontSize = '24px';
                        }
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: '24px' }}>{app.icon}</span>
                  )}
                </div>
                <span className="program-name">{app.name}</span>
                <div className="program-arrow">›</div>
              </div>
            ))}
          </div>
        </div>

        <div className="recent-section">
          <h3>Recently Used</h3>
          <div className="recent-list">
            {recentApps.length > 0 ? (
              recentApps.map((app) => (
                <div key={app.id} className="recent-item" onClick={() => {
                  onAppClick(app.id);
                  onClose();
                }}>
                  <div className="recent-icon">
                    {app.isCustomIcon ? (
                      <img
                        src={app.icon}
                        alt={app.name}
                        width="20"
                        height="20"
                        style={{
                          display: 'block',
                          filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))'
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = '🔗';
                            parent.style.fontSize = '20px';
                          }
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: '20px' }}>{app.icon}</span>
                    )}
                  </div>
                  <div className="recent-info">
                    <span className="recent-name">{app.name}</span>
                    <span className="recent-subtitle">Opened {formatLastUsed(app.lastUsed)}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="recent-item">
                <div className="recent-icon">📁</div>
                <div className="recent-info">
                  <span className="recent-name">No recent items</span>
                  <span className="recent-subtitle">Click an app to see it here</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="start-menu-footer">
        {user ? (
          <button className="sign-out-button" onClick={() => { signOut(); onClose(); }}>
            <span className="sign-out-icon">🚪</span>
            <span>Sign out</span>
          </button>
        ) : (
          <button className="sign-in-button" onClick={() => { signIn(); onClose(); }}>
            <span className="sign-in-icon">🔑</span>
            <span>Sign in</span>
          </button>
        )}
        <button className="power-button">
          <span className="power-icon">⏻</span>
        </button>
      </div>
    </div>
  );
};

export default StartMenu;
